import { Accordion } from '@tvg/fuse/accordion';
import { EUserAction } from 'client/config';
import useComponentId from 'client/hooks/useComponentId';
import { usePLPNavigate } from 'client/hooks/usePLPNavigate';
import CheckboxGroup from 'client/pages/CheckboxGroup/CheckboxGroup';
import { useDrawerState } from 'client/state/Drawer';
import { useFacetListState } from 'client/state/FacetList';
import { ACCORDION_TITLE_SIZE } from 'client/utils/consts';
import { PLPGeneralFacetValue } from 'common/types/plp-data';
import React, { useCallback } from 'react';
import styles from './FacetItemCategory.module.css';

export type FacetItemCategoryProps = {
  id: string;
  name: string;
  values?: PLPGeneralFacetValue[];
  isDrawerView?: boolean;
};

function FacetItemCategory({ id, name, values = [], isDrawerView = false }: FacetItemCategoryProps) {
  const plpNavigate = usePLPNavigate();
  const toggleShowViewProducts = useDrawerState((s) => s.toggleShowViewProducts);
  const { activeFacet, setActiveFacet } = useFacetListState();
  const headingName = name === 'All' ? 'Department' : `${name}`;
  const componentId = useComponentId(`facet-accordion-${id}-${name}`);

  const updateState = useCallback(
    (value: string) => {
      toggleShowViewProducts.on();
      plpNavigate({ category: value }, EUserAction.CATEGORY);
    },
    [plpNavigate, toggleShowViewProducts],
  );

  const handleChangeCallback = useCallback(
    (e: React.FormEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault(); // To avoid following the SEO URL

      const button = e.currentTarget as HTMLButtonElement;
      const value = button.dataset.value || '';

      updateState(value);
    },
    [updateState],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Enter') {
        e.stopPropagation();
        e.preventDefault(); // To avoid following the SEO URL
        const value = (e.target as HTMLInputElement).getAttribute('data-value');
        if (value) updateState(value);
      }
    },
    [updateState],
  );

  const onClickAccordion = useCallback(() => setActiveFacet(id || name), [id, name, setActiveFacet]);

  return (
    <Accordion
      className={styles.facetItemCategory__accordion}
      heading={headingName}
      open={(id || name) === activeFacet}
      onClick={onClickAccordion}
      dataTestId="facet-accordion"
      headingTag={isDrawerView ? 'h2' : 'h3'}
      titleSize={ACCORDION_TITLE_SIZE}
      componentId={componentId}
    >
      <div className={styles.facetItemCategory__spacing} />
      <CheckboxGroup
        className={styles.facetItemCategory__checkboxGroup}
        isShowCheckbox={false}
        groupDescription={`Filters for ${headingName}`}
        items={values}
        isFullWidth
        onChange={handleChangeCallback}
        onKeyDown={handleKeyDown}
        dataTestId="facet-button-category"
      />
    </Accordion>
  );
}

export default React.memo(FacetItemCategory);
